(() => {
  if (customElements.get('cpap-runtime')) return;

  const positiveNumber = (raw) => {
    if (typeof raw !== 'string' || raw.trim() === '') return null;
    const value = Number(raw);
    return Number.isFinite(value) && value > 0 ? value : null;
  };

  class CpapRuntime extends HTMLElement {
    connectedCallback() {
      this.frame = requestAnimationFrame(() => this.initialize());
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.controller?.abort();
    }

    initialize() {
      if (!this.isConnected) return;
      this.wattsInput = this.querySelector('[data-runtime-watts]');
      this.hoursInput = this.querySelector('[data-runtime-hours-input]');
      this.error = this.querySelector('[data-runtime-error]');
      if (!this.wattsInput || !this.hoursInput || !this.error) return;

      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.results = Array.from(this.querySelectorAll('[data-runtime-result]'));
      this.presets = Array.from(this.querySelectorAll('[data-preset]'));
      this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
        useGrouping: false,
      });

      this.wattsInput.addEventListener('input', () => this.update(), options);
      this.hoursInput.addEventListener('input', () => this.update(), options);
      for (const preset of this.presets) {
        preset.addEventListener('click', () => {
          if (preset.dataset.preset === 'custom') {
            this.selectPreset(preset);
            this.wattsInput.focus();
            return;
          }
          this.wattsInput.value = preset.dataset.preset;
          this.update();
        }, options);
      }

      this.update();
      for (const control of this.querySelectorAll('[data-runtime-controls]')) control.hidden = false;
      const defaults = this.querySelector('[data-runtime-defaults]');
      if (defaults) defaults.hidden = true;
    }

    selectPreset(activePreset) {
      for (const preset of this.presets) {
        preset.setAttribute('aria-pressed', String(preset === activePreset));
      }
    }

    update() {
      const watts = positiveNumber(this.wattsInput.value);
      const hoursPerNight = positiveNumber(this.hoursInput.value);
      const efficiency = positiveNumber(this.dataset.efficiency);
      const invalidWatts = watts === null;
      const invalidHours = hoursPerNight === null || hoursPerNight > 24;
      let errorKey = '';
      if (invalidWatts) errorKey = 'invalidWatts';
      else if (invalidHours) errorKey = 'invalidHours';
      else if (efficiency === null || efficiency > 100) errorKey = 'invalidSettings';

      this.wattsInput.setAttribute('aria-invalid', String(invalidWatts));
      this.hoursInput.setAttribute('aria-invalid', String(invalidHours));
      this.error.textContent = this.dataset[errorKey] || '';
      this.error.hidden = !errorKey;

      const matchingPreset = this.presets.find((preset) =>
        preset.dataset.preset !== 'custom' && positiveNumber(preset.dataset.preset) === watts && watts !== null
      );
      this.selectPreset(matchingPreset || this.presets.find((preset) => preset.dataset.preset === 'custom'));

      for (const result of this.results) {
        const capacity = positiveNumber(result.dataset.capacity);
        const output = positiveNumber(result.dataset.output);
        let resultErrorKey = errorKey;
        if (!resultErrorKey && capacity === null) resultErrorKey = 'missingCapacity';
        if (!resultErrorKey && output !== null && watts > output) resultErrorKey = 'overpower';

        let hours;
        let nights;
        if (!resultErrorKey) {
          hours = capacity * (efficiency / 100) / watts;
          nights = hours / hoursPerNight;
          if (!Number.isFinite(hours) || !Number.isFinite(nights)) resultErrorKey = 'invalidSettings';
        }

        const values = result.querySelector('[data-runtime-values]');
        const message = result.querySelector('[data-runtime-message]');
        const note = result.querySelector('[data-runtime-note]');
        if (!values || !message || !note) continue;
        values.hidden = Boolean(resultErrorKey);
        message.textContent = this.dataset[resultErrorKey] || '';
        message.hidden = !resultErrorKey;
        note.textContent = this.dataset.unknownOutput;
        note.hidden = Boolean(resultErrorKey) || output !== null;
        if (!resultErrorKey) {
          const hoursValue = result.querySelector('[data-runtime-hours]');
          const nightsValue = result.querySelector('[data-runtime-nights]');
          if (hoursValue) hoursValue.textContent = this.formatter.format(hours);
          if (nightsValue) nightsValue.textContent = this.formatter.format(nights);
        }
      }
    }
  }

  customElements.define('cpap-runtime', CpapRuntime);
})();

(() => {
  if (customElements.get('cpap-model-carousel')) return;

  class CpapModelCarousel extends HTMLElement {
    connectedCallback() {
      this.frame = requestAnimationFrame(() => this.initialize());
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.controller?.abort();
      this.observer?.disconnect();
    }

    initialize() {
      if (!this.isConnected) return;
      this.track = this.querySelector('[data-comparison-track]');
      this.previous = this.querySelector('[data-comparison-previous]');
      this.next = this.querySelector('[data-comparison-next]');
      if (!this.track || !this.previous || !this.next) return;

      this.controller?.abort();
      this.observer?.disconnect();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.previous.addEventListener('click', () => this.move(-1), options);
      this.next.addEventListener('click', () => this.move(1), options);
      this.track.addEventListener('scroll', () => this.update(), { ...options, passive: true });
      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track) return;
        const rtl = getComputedStyle(this.track).direction === 'rtl';
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.move((event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1));
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          this.track.scrollTo({ left: event.key === 'Home' ? 0 : (rtl ? -1 : 1) * this.track.scrollWidth, behavior: this.behavior });
        }
      }, options);
      this.observer = new ResizeObserver(() => this.update());
      this.observer.observe(this.track);
      this.querySelector('[data-comparison-controls]').hidden = false;
      this.update();
    }

    get behavior() {
      return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
    }

    move(direction) {
      const cards = this.track.children;
      if (cards.length < 2) return;
      const step = Math.abs(cards[1].getBoundingClientRect().left - cards[0].getBoundingClientRect().left);
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      this.track.scrollBy({ left: direction * step * (rtl ? -1 : 1), behavior: this.behavior });
    }

    update() {
      const position = Math.abs(this.track.scrollLeft);
      this.previous.disabled = position < 2;
      this.next.disabled = position >= this.track.scrollWidth - this.track.clientWidth - 2;
    }
  }

  customElements.define('cpap-model-carousel', CpapModelCarousel);
})();
